import { createId } from '@paralleldrive/cuid2';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { Icon, ICONS } from 'components/Icon';
import { Menu, type MenuProps } from 'components/Menu';
import { useScreenSize } from 'hooks/useScreenSize';
import { UserAvatar } from 'components/UserAvatar';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doTaskAssignedCommentCreate,
  doTaskAssignedCommentCreateFailure,
  doTaskAssignedCommentCreateSuccess,
  doTaskAssignedCommentDraftAttachmentUpload,
} from 'store/tasks/actions';
import {
  ALERT,
  doAppFileGetFilesFromPicker,
  doAppFileGetImagesFromPicker,
  doAssetFileGetPhotoFromPhoto,
  eventFilePickerFinished,
  eventFilesAdded,
} from 'store/actions';
import {
  getFileBeingAddedFromDroppedFile,
  useFileDropTarget,
  validateFile,
} from 'service/file';
import type { FileBeingAdded } from 'service/file/types';
import {
  TASK_COMMENT_ATTACHMENT_MAX_COUNT,
  TASK_COMMENT_ATTACHMENT_MAX_SIZE_BYTES,
  type TaskDetailsResponse,
} from 'types/Tasks';
import { randomKey } from 'lib/riskbase';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { TaskAssignedCommentAttachments } from './TaskAssignedCommentAttachments';
import { CardRow } from 'components/CardRow';
import { removeFileUpload } from 'store/fileUpload/actions';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';

const DROP_TARGET_ID = 'task-assigned-comment-composer-drop-target';
const TABLET_MAX_WIDTH = 1400;
const COMMENT_ATTACHMENT_LIMIT_ERROR = `You can attach up to ${TASK_COMMENT_ATTACHMENT_MAX_COUNT} files.`;

export const TaskAssignedCommentComposer: React.FC<{
  taskId: TaskDetailsResponse['id'];
}> = ({ taskId }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize(true);
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = screenSize !== 'large';
  const isTablet = screenWidth <= TABLET_MAX_WIDTH;
  const showUserAvatar = !isMobile && !isTablet;

  const isWeb = Platform.OS === 'web';
  const [composerId, setComposerId] = React.useState(randomKey());
  const isActiveRef = React.useRef(true);
  const submissionIdRef = React.useRef<string | null>(null);
  const isSubmittingRef = React.useRef(false);
  const isAttachmentPickerActiveRef = React.useRef(false);
  const uploadIdsRef = React.useRef<string[]>([]);
  const [comment, setComment] = React.useState('');
  const [uploadIds, setUploadIds] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isAttachmentPickerActive, setIsAttachmentPickerActive] =
    React.useState(false);
  uploadIdsRef.current = uploadIds;
  const hasCommentText = !!comment.trim();

  React.useEffect(() => {
    isActiveRef.current = true;
    setComment('');
    setUploadIds([]);
    setIsSubmitting(false);
    setIsAttachmentPickerActive(false);
    isAttachmentPickerActiveRef.current = false;
    isSubmittingRef.current = false;
    submissionIdRef.current = null;

    return () => {
      isActiveRef.current = false;
      const discardedUploadIds = uploadIdsRef.current;
      uploadIdsRef.current = [];
      discardedUploadIds.forEach((uploadId) => {
        dispatch(removeFileUpload({ uploadId }));
      });
    };
  }, [dispatch, taskId]);

  const beginAttachmentPicker = React.useCallback(() => {
    if (isAttachmentPickerActiveRef.current) {
      return false;
    }
    isAttachmentPickerActiveRef.current = true;
    setIsAttachmentPickerActive(true);
    return true;
  }, []);

  const finishAttachmentPicker = React.useCallback(() => {
    isAttachmentPickerActiveRef.current = false;
    if (isActiveRef.current) {
      setIsAttachmentPickerActive(false);
    }
  }, []);

  React.useEffect(() => {
    const successEvent = doAddEvent(
      doTaskAssignedCommentCreateSuccess,
      (action) => {
        if (
          action.payload.taskId === taskId &&
          action.payload.composerId === composerId &&
          action.payload.submissionId === submissionIdRef.current
        ) {
          submissionIdRef.current = null;
          uploadIdsRef.current = [];
          setComposerId(randomKey());
          setComment('');
          setUploadIds([]);
          setIsSubmitting(false);
          isSubmittingRef.current = false;
        }
      },
    );
    const failureEvent = doAddEvent(
      doTaskAssignedCommentCreateFailure,
      (action) => {
        if (
          action.payload.taskId === taskId &&
          action.payload.composerId === composerId &&
          action.payload.submissionId === submissionIdRef.current
        ) {
          submissionIdRef.current = null;
          setIsSubmitting(false);
          isSubmittingRef.current = false;
        }
      },
    );
    return () => {
      doRemoveEvent(successEvent);
      doRemoveEvent(failureEvent);
    };
  }, [composerId, taskId]);

  const handleSend = React.useCallback(() => {
    if (isSubmitting) {
      return;
    }

    if (!hasCommentText) {
      dispatch(
        ALERT({
          title: 'Comment Required',
          text: 'You need to add text to submit a comment.',
        }),
      );
      return;
    }

    const submissionId = createId();
    submissionIdRef.current = submissionId;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    dispatch(
      doTaskAssignedCommentCreate({
        taskId,
        composerId,
        submissionId,
        comment,
        uploadIds: [...uploadIds],
      }),
    );
  }, [
    comment,
    composerId,
    dispatch,
    hasCommentText,
    isSubmitting,
    taskId,
    uploadIds,
  ]);

  const addAttachments = React.useCallback(
    (incomingFiles: FileBeingAdded[]) => {
      if (!isActiveRef.current || isSubmittingRef.current) {
        return;
      }

      const errors: string[] = [];

      const availableSlots =
        TASK_COMMENT_ATTACHMENT_MAX_COUNT - uploadIdsRef.current.length;
      if (incomingFiles.length > availableSlots) {
        errors.push(COMMENT_ATTACHMENT_LIMIT_ERROR);
      }

      incomingFiles.slice(0, Math.max(0, availableSlots)).forEach((file) => {
        const validationError = validateFile(file, {
          maxFileSizeBytes: TASK_COMMENT_ATTACHMENT_MAX_SIZE_BYTES,
        });

        if (validationError) {
          errors.push(`${file.name}: ${validationError.error}`);
          return;
        }

        const uploadId = createId();
        const nextUploadIds = [...uploadIdsRef.current, uploadId];
        uploadIdsRef.current = nextUploadIds;
        setUploadIds(nextUploadIds);
        dispatch(
          doTaskAssignedCommentDraftAttachmentUpload({
            taskId,
            uploadId,
            file,
          }),
        );
      });

      if (errors.length) {
        dispatch(
          ALERT({
            title: 'File Error',
            text: errors.join('\n'),
          }),
        );
      }
    },
    [dispatch, taskId],
  );

  React.useEffect(() => {
    const filesAddedEvent = doAddEvent(eventFilesAdded, (action) => {
      if (action.payload.key === composerId) {
        addAttachments(action.payload.files);
      }
    });
    const pickerFinishedEvent = doAddEvent(
      eventFilePickerFinished,
      (action) => {
        if (action.payload.key === composerId) {
          finishAttachmentPicker();
        }
      },
    );
    return () => {
      doRemoveEvent(filesAddedEvent);
      doRemoveEvent(pickerFinishedEvent);
    };
  }, [addAttachments, composerId, finishAttachmentPicker]);

  const handleTakePhoto = React.useCallback(() => {
    dispatch(
      doAssetFileGetPhotoFromPhoto({
        eventKey: composerId,
        returnType: 'event',
      }),
    );
  }, [composerId, dispatch]);

  const handleSelectPhotos = React.useCallback(() => {
    dispatch(
      doAppFileGetImagesFromPicker({
        eventKey: composerId,
        multiSelect: true,
        allowVideo: true,
        returnType: 'event',
      }),
    );
  }, [composerId, dispatch]);

  const handleSelectAttachments = React.useCallback(() => {
    dispatch(
      doAppFileGetFilesFromPicker({
        eventKey: composerId,
        multiSelect: true,
        allowedFileGroups: null,
        returnType: 'event',
      }),
    );
  }, [composerId, dispatch]);

  const openPickerFromMenu = React.useCallback(
    (openPicker: () => void) => {
      if (beginAttachmentPicker()) {
        openPicker();
      }
    },
    [beginAttachmentPicker],
  );

  const handleSelectPhotosFromMenu = React.useCallback(() => {
    openPickerFromMenu(handleSelectPhotos);
  }, [handleSelectPhotos, openPickerFromMenu]);

  const handleSelectAttachmentsFromMenu = React.useCallback(() => {
    openPickerFromMenu(handleSelectAttachments);
  }, [handleSelectAttachments, openPickerFromMenu]);

  const handleTakePhotoFromMenu = React.useCallback(() => {
    openPickerFromMenu(handleTakePhoto);
  }, [handleTakePhoto, openPickerFromMenu]);

  const nativeAttachmentMenuOptions = React.useMemo<MenuProps['options']>(
    () => [
      {
        key: 'camera',
        text: 'Take Photo',
        icon: ICONS.CameraLight,
        onPress: handleTakePhotoFromMenu,
      },
      {
        key: 'photos',
        text: 'Choose Photos and Videos',
        icon: ICONS.ImageLight,
        onPress: handleSelectPhotosFromMenu,
      },
      {
        key: 'files',
        text: 'Attach Files',
        icon: ICONS.FolderOpenLight,
        onPress: handleSelectAttachmentsFromMenu,
      },
    ],
    [
      handleSelectAttachmentsFromMenu,
      handleSelectPhotosFromMenu,
      handleTakePhotoFromMenu,
    ],
  );

  const handleDropFiles = React.useCallback(
    (droppedFiles: File[]) => {
      addAttachments(droppedFiles.map(getFileBeingAddedFromDroppedFile));
    },
    [addAttachments],
  );

  const { isDraggingFile, isDraggingOverTarget: isDraggingOverComposer } =
    useFileDropTarget({
      enabled: isWeb && !isSubmitting,
      onDropFiles: handleDropFiles,
      targetId: DROP_TARGET_ID,
    });

  const handleAttachmentCancelled = React.useCallback((uploadId: string) => {
    const nextUploadIds = uploadIdsRef.current.filter(
      (currentUploadId) => currentUploadId !== uploadId,
    );
    uploadIdsRef.current = nextUploadIds;
    setUploadIds(nextUploadIds);
  }, []);

  return (
    <Card
      bodyStyle={{
        backgroundColor: theme.colors.white,
      }}
      separator={<View />}
    >
      <CardRow
        contentStyle={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0 }}
        style={{
          paddingLeft: 0,
          paddingRight: 0,
        }}
      >
        <View style={styles.composerRow}>
          {showUserAvatar && (
            <UserAvatar
              previewOnPress={false}
              size={40}
              style={{ margin: theme.padding, marginRight: 0 }}
            />
          )}
          <View nativeID={DROP_TARGET_ID} style={styles.container}>
            {isDraggingFile && (
              <View
                pointerEvents="none"
                style={[
                  styles.dropOverlay,
                  isDraggingOverComposer && styles.dropOverlayOver,
                  showUserAvatar && {
                    left: theme.padding / 2,
                    right: theme.padding / 2,
                    top: theme.padding / 2,
                    bottom: theme.padding / 2,
                  },
                ]}
              >
                <Icon
                  source={ICONS.FilePlusUpload}
                  size={28}
                  color={
                    isDraggingOverComposer
                      ? theme.colors.white
                      : theme.colors.blue
                  }
                />
              </View>
            )}
            <View
              style={[
                styles.commentRow,
                showUserAvatar ? { marginTop: theme.padding / 2 } : undefined,
              ]}
            >
              <View style={{ flex: 1 }}>
                <TextAreaField
                  key={composerId}
                  value={comment}
                  onChange={setComment}
                  placeholder="Add a comment…"
                  label="Comment"
                  maxLength={25000}
                  showFieldTypeIcon={false}
                  disabled={isSubmitting}
                />
              </View>
              <View style={styles.actions}>
                {isWeb ? (
                  <Button
                    icon={ICONS.PaperClipLight}
                    iconColor={theme.colors.blue}
                    onPress={handleSelectAttachmentsFromMenu}
                    disabled={isSubmitting || isAttachmentPickerActive}
                    accessibilityLabel="Attach files"
                  />
                ) : (
                  <Menu
                    buttonConfig={{
                      icon: ICONS.PaperClipLight,
                      iconColor: theme.colors.blue,
                      accessibilityLabel: 'Attachment options',
                    }}
                    disabled={isSubmitting || isAttachmentPickerActive}
                    options={nativeAttachmentMenuOptions}
                  />
                )}
                <View style={{ width: 43 }}>
                  {isSubmitting ? (
                    <ActivityIndicator size="small" />
                  ) : (
                    <Button
                      icon={ICONS.PaperPlaneLight}
                      iconColor={theme.colors.blue}
                      onPress={handleSend}
                      accessibilityLabel="Create comment"
                    />
                  )}
                </View>
              </View>
            </View>
          </View>
        </View>
      </CardRow>
      <TaskAssignedCommentAttachments
        uploadIds={uploadIds}
        onCancelled={handleAttachmentCancelled}
        cancelDisabled={isSubmitting}
      />
    </Card>
  );
};

const styles = StyleSheet.create({
  composerRow: {
    alignItems: 'flex-start',
    flex: 1,
    flexDirection: 'row',
  },
  container: {
    backgroundColor: theme.colors.white,
    flex: 1,
    position: 'relative',
    alignSelf: 'stretch',
  },
  dropOverlay: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    backgroundColor: theme.colors.white,
    borderRadius: theme.cardBorderRadius,
    outlineColor: theme.colors.blue,
    outlineOffset: -2,
    outlineWidth: 2,
    outlineStyle: 'dashed',
  },
  dropOverlayOver: {
    backgroundColor: theme.colors.blue,
  },
  commentRow: {
    alignItems: 'flex-end',
    flexDirection: 'row',
  },
  actions: {
    alignItems: 'center',
    flexDirection: 'row',
    height: 50,
    marginRight: theme.padding,
  },
});
